import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Bell,
} from 'lucide-react';
import { useListIntegrationConnections } from '../../../../api/curated-integrations.api';
import type { IntegrationConnectionItemDto } from '../../../../api/curated-integrations.types';

interface ConnectionsTabProps {
  /** UUID of the integration. */
  integrationId: string;
}

type ConnectionStatus = NonNullable<IntegrationConnectionItemDto['status']>;

function ConnectionStatusBadge({ status }: { status: ConnectionStatus | undefined }) {
  if (!status) return null;

  switch (status) {
    case 'connected':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" aria-hidden="true" />
          Verbunden
        </Badge>
      );
    case 'pending':
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 flex items-center gap-1">
          <Clock className="w-3 h-3" aria-hidden="true" />
          Ausstehend
        </Badge>
      );
    case 'auth_in_progress':
      return (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
          Autorisierung läuft
        </Badge>
      );
    case 'error':
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200 flex items-center gap-1">
          <XCircle className="w-3 h-3" aria-hidden="true" />
          Fehler
        </Badge>
      );
    case 'disconnected':
      return (
        <Badge className="bg-gray-100 text-gray-600 border-gray-200 flex items-center gap-1">
          <XCircle className="w-3 h-3" aria-hidden="true" />
          Getrennt
        </Badge>
      );
    default:
      return (
        <Badge className="bg-gray-100 text-gray-600 border-gray-200">{status}</Badge>
      );
  }
}

/**
 * Tab "Connections" der Integration-Detail-Page.
 * Read-only-Tabelle aller Kunden-AgentMcpConnections für diese Integration.
 */
export function ConnectionsTab({ integrationId }: ConnectionsTabProps) {
  const {
    data: connections,
    isLoading,
    error,
    refetch,
  } = useListIntegrationConnections(integrationId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" aria-label="Verbindungen werden geladen…" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
      >
        <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        <span className="text-sm">Fehler beim Laden: {error.message}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => void refetch()}
          aria-label="Verbindungen neu laden"
        >
          Wiederholen
        </Button>
      </div>
    );
  }

  const list = connections ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Aktive Verbindungen</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {list.length === 0
              ? 'Noch keine Kunden mit dieser Integration verbunden.'
              : `${list.length} Verbindung${list.length !== 1 ? 'en' : ''}`}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          aria-label="Verbindungen aktualisieren"
        >
          <RefreshCw className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>

      {list.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-6 h-6 text-gray-400" aria-hidden="true" />
            </div>
            <p className="text-gray-500 font-medium">Keine Verbindungen</p>
            <p className="text-gray-400 text-sm mt-1">
              Sobald Kunden diese Integration mit einem Agenten verbinden, erscheinen sie hier.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                role="table"
                aria-label="Verbindungsübersicht"
              >
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-600">
                      Agent
                    </th>
                    <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-600">
                      Kunde
                    </th>
                    <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-600">
                      Status
                    </th>
                    <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-600">
                      Neue Methoden
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((conn, index) => (
                    <tr
                      key={conn.agentId ?? index}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">
                          {conn.agentName ?? <span className="text-gray-400 italic">—</span>}
                        </div>
                        {conn.agentId && (
                          <div className="text-xs text-gray-400 font-mono mt-0.5">
                            {conn.agentId}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-700">
                          {conn.customerName ?? <span className="text-gray-400 italic">—</span>}
                        </div>
                        {conn.customerId && (
                          <div className="text-xs text-gray-400 font-mono mt-0.5">
                            {conn.customerId}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <ConnectionStatusBadge status={conn.status as ConnectionStatus} />
                      </td>
                      <td className="py-3 px-4">
                        {conn.hasNewMethodsAvailable ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5"
                            aria-label="Neue Methoden verfügbar"
                          >
                            <Bell className="w-3 h-3" aria-hidden="true" />
                            Verfügbar
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
